import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { RouteName } from "constant/routeNames";

// Icons
import {
  FiMonitor,
  FiCreditCard,
  FiLock,
  FiUserPlus,
  FiChevronRight,
  FiDownload,
  FiFileText,
} from "react-icons/fi";
import { MdOutlineDevices } from "react-icons/md";

// Services
import PaymentService from "services/PaymentService";
import PayperviewService from "services/payperview/PayperviewServie";

// Existing components
import PayPerViewCard from "pages/PayPerView/shared/PayPerViewCard";
import PayperviewPaidSkeleton from "components/skeleton/PayperviewPaidSkeleton";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (dateStr: string) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatShortDate = (dateStr: string) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatMemberSince = (dateStr: string) => {
  if (!dateStr) return "";
  return new Date(dateStr)
    .toLocaleDateString("en-US", { month: "long", year: "numeric" })
    .toUpperCase();
};

const PAGE_SIZE = 10;

// ─── Tab Types ───────────────────────────────────────────────────────────────

type Tab = "paid-subscriptions" | "transactions";

const PurchasedEventsTab = () => {
  const navigate = useNavigate();
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const [purchasedEvents, setPurchasedEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    PayperviewService.getPurchasedEvents(loggedInUser?.id)
      .then((res: any) => {
        setPurchasedEvents(res.data || []);
      })
      .catch(() => setPurchasedEvents([]))
      .finally(() => setIsLoading(false));
  }, [loggedInUser?.id]);

  return (
    <div>
      <h2 className="mb-6 text-xl font-semibold text-white">
        Purchased Events
      </h2>

      {isLoading && <PayperviewPaidSkeleton />}

      {!isLoading && purchasedEvents.length === 0 && (
        <div className="flex flex-col items-center gap-5 mt-16">
          <p className="text-sm text-gray-500">No purchased events found.</p>
          <button
            onClick={() => navigate(RouteName.PayPerView.replace("/*", ""))}
            className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#e30030] to-[#c70025] text-white text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Purchase
          </button>
        </div>
      )}

      {!isLoading && purchasedEvents.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4">
          {purchasedEvents.map((event: any, index: number) => (
            <div key={index}>
              <PayPerViewCard event={event} isPurchased={true} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Transactions Tab ─────────────────────────────────────────────────────────

const TransactionsTab = () => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setIsLoading(true);
    PayperviewService.paymentTransection()
      .then((res: any) => {
        setTransactions(res?.transactions?.data || []);
      })
      .catch(() => setTransactions([]))
      .finally(() => setIsLoading(false));
  }, []);

  const totalPages = Math.ceil(transactions.length / PAGE_SIZE);
  const paginatedRows = transactions.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const handleExportCSV = () => {
    if (transactions.length === 0) return;
    const header = ["Date", "Description", "Amount", "Status", "Order Number"];
    const rows = transactions.map((t: any) => [
      formatShortDate(t.purchased_formatted_date),
      t.content_name,
      `$${t.amount}`,
      t.payment_status,
      t.order_number,
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "billing-history.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusStyle = (status: string) => {
    const s = status?.toLowerCase();
    if (s === "paid" || s === "completed")
      return "bg-green-500/10 text-green-400 border border-green-500/20";
    if (s === "expired")
      return "bg-red-500/10 text-red-400 border border-red-500/20";
    if (s === "refunded")
      return "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20";
    return "bg-gray-500/10 text-gray-400 border border-gray-500/20";
  };

  const getStatusLabel = (status: string) => {
    const s = status?.toLowerCase();
    if (s === "paid") return "COMPLETED";
    return status?.toUpperCase();
  };

  const getDescription = (t: any) => {
    const name = t.content_name || "Unknown";
    const sub = t.order_number ? "Subscription Plan" : "One-time Purchase";
    return { title: name, subtitle: sub };
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">Payment History</h2>
        <button
          onClick={handleExportCSV}
          disabled={transactions.length === 0}
          className="flex items-center gap-2 text-sm text-gray-400 transition-colors hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <FiDownload size={15} />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="rounded-xl bg-[#141414] border border-[#242424] overflow-hidden">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-4 px-6 py-5 border-b border-[#1f1f1f] last:border-0"
            >
              <div className="h-3 w-24 bg-[#222] animate-pulse rounded" />
              <div className="flex-1 h-3 bg-[#222] animate-pulse rounded" />
              <div className="h-3 w-16 bg-[#222] animate-pulse rounded" />
              <div className="h-6 w-24 bg-[#222] animate-pulse rounded-full" />
              <div className="h-8 w-8 bg-[#222] animate-pulse rounded-lg" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && transactions.length === 0 && (
        <div className="flex flex-col items-center gap-3 mt-16">
          <p className="text-sm text-gray-500">No transactions found.</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && transactions.length > 0 && (
        <div className="rounded-2xl bg-[#141414] border border-[#242424] overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[130px_1fr_100px_130px_60px] gap-4 px-6 py-3 bg-[#111] border-b border-[#1f1f1f]">
            <span className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              Date
            </span>
            <span className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              Description
            </span>
            <span className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              Amount
            </span>
            <span className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              Status
            </span>
            <span className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              Receipt
            </span>
          </div>

          {/* Rows */}
          <div className="divide-y divide-[#1a1a1a]">
            {paginatedRows.map((t: any, i: number) => {
              const desc = getDescription(t);
              return (
                <div
                  key={i}
                  className="grid grid-cols-[130px_1fr_100px_130px_60px] gap-4 px-6 py-4 items-center hover:bg-[#191919] transition-colors"
                >
                  {/* Date */}
                  <span className="text-sm text-gray-300">
                    {formatShortDate(t.purchased_formatted_date)}
                  </span>

                  {/* Description */}
                  <div>
                    <p className="text-sm font-medium text-white">
                      {desc.title}
                    </p>
                  </div>

                  {/* Amount */}
                  <span className="text-sm font-medium text-white">
                    £{t.amount}
                  </span>

                  {/* Status */}
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide w-fit ${getStatusStyle(
                      t.payment_status,
                    )}`}
                  >
                    {getStatusLabel(t.payment_status)}
                  </span>

                  {/* Receipt */}
                  <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#1e1e1e] border border-[#2a2a2a] text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                    <FiFileText size={14} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Pagination footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#1f1f1f]">
            <span className="text-xs text-gray-500">
              Showing{" "}
              {Math.min((currentPage - 1) * PAGE_SIZE + 1, transactions.length)}
              –{Math.min(currentPage * PAGE_SIZE, transactions.length)} of{" "}
              {transactions.length} transactions
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-1.5 text-sm text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="px-4 py-1.5 text-sm text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Member Page ────────────────────────────────────────────────────────

const PpvPage = () => {
  const [activeTab, setActiveTab] = useState<Tab>("paid-subscriptions");
  const FE_URL = process.env.REACT_APP_FRONTEND_URL;

  const tabs: { id: Tab; label: string }[] = [
    { id: "paid-subscriptions", label: "Purchased Events" },
    { id: "transactions", label: "Transactions" },
  ];

  return (
    <>
      <Helmet>
        <title>PPV Events — AYOZAT</title>
        <meta
          name="description"
          content="Manage your AYOZAT membership, subscriptions, and billing history."
        />
        <link rel="canonical" href={`${FE_URL}/membership`} />
        <meta property="og:title" content="Membership — AYOZAT" />
        <meta
          property="og:description"
          content="Manage your AYOZAT membership, subscriptions, and billing history."
        />
        <meta property="og:image" content="https://ayozat.co.uk/logo192.png" />
        <meta property="og:url" content={`${FE_URL}/membership`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen px-4 py-8 text-white">
        {/* Tab bar */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-[#e30030] text-white"
                  : "bg-transparent border border-[#3a3a3a] text-white hover:border-gray-500"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="transition-all duration-200">
          {/* {activeTab === "overview" && <OverviewTab />} */}
          {activeTab === "paid-subscriptions" && <PurchasedEventsTab />}
          {activeTab === "transactions" && <TransactionsTab />}
        </div>
      </div>
    </>
  );
};

export default PpvPage;
